import axios, { AxiosError } from 'axios';
import baseUrl from '../config/baseURL';
import { getAuthToken } from '../services/auth';
import { Order, OrderItem } from '../utils/types';

type OrderListParams = {
  status?: string;
  source?: string;
  payment_status?: string;
  fulfillment_status?: string;
  priority?: string;
  ordering?: string;
  search?: string;
  q?: string;
};

type ApiResult<T> = {
  success: boolean;
  data: T;
};

const getHeaders = async () => {
  const token = await getAuthToken();
  if (!token?.success || !token.data) {
    return null;
  }

  return {
    Authorization: `Bearer ${token.data}`,
    'Content-Type': 'application/json',
  };
};

const getErrorPayload = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return error.response?.data || error.message;
  }

  return error;
};

const getOrderBaseEndpoint = (organizationId: string) =>
  `${baseUrl}/core/organizations/${organizationId}/orders/`;

const getOrderItemEndpoint = (organizationId: string, orderId: string) =>
  `${baseUrl}/core/organizations/${organizationId}/orders/${orderId}/items/`;

export const getOrders = async (
  organizationId?: string,
  params: OrderListParams = {},
): Promise<ApiResult<Order[] | { results: Order[] } | unknown>> => {
  if (!organizationId) {
    return { success: false, data: 'Organization ID missing' };
  }

  const headers = await getHeaders();
  if (!headers) {
    return { success: false, data: 'Token does not exist' };
  }

  try {
    const response = await axios.get(getOrderBaseEndpoint(organizationId), {
      headers,
      params,
    });

    return { success: response.status >= 200 && response.status < 300, data: response.data };
  } catch (error) {
    console.error('getOrders error', error);
    return { success: false, data: getErrorPayload(error) };
  }
};

export const getOrderById = async (
  organizationId?: string,
  orderId?: string,
): Promise<ApiResult<Order | unknown>> => {
  if (!organizationId || !orderId) {
    return { success: false, data: 'Organization ID or order ID missing' };
  }

  const headers = await getHeaders();
  if (!headers) {
    return { success: false, data: 'Token does not exist' };
  }

  try {
    const response = await axios.get(`${getOrderBaseEndpoint(organizationId)}${orderId}/`, {
      headers,
    });

    return { success: response.status >= 200 && response.status < 300, data: response.data };
  } catch (error) {
    console.error('getOrderById error', error);
    return { success: false, data: getErrorPayload(error) };
  }
};

export const createOrder = async (
  organizationId?: string,
  data?: Record<string, unknown>,
): Promise<ApiResult<Order | unknown>> => {
  if (!organizationId) {
    return { success: false, data: 'Organization ID missing' };
  }

  const headers = await getHeaders();
  if (!headers) {
    return { success: false, data: 'Token not found' };
  }

  try {
    const response = await axios.post(getOrderBaseEndpoint(organizationId), data, { headers });
    return { success: response.status >= 200 && response.status < 300, data: response.data };
  } catch (error) {
    console.error('createOrder error', error);
    return { success: false, data: getErrorPayload(error) };
  }
};

export const updateOrder = async (
  organizationId?: string,
  orderId?: string,
  payload?: Record<string, unknown>,
): Promise<ApiResult<Order | unknown>> => {
  if (!organizationId || !orderId) {
    return { success: false, data: 'Organization ID or order ID missing' };
  }

  const headers = await getHeaders();
  if (!headers) {
    return { success: false, data: 'Token does not exist' };
  }

  try {
    const response = await axios.patch(`${getOrderBaseEndpoint(organizationId)}${orderId}/`, payload, {
      headers,
    });

    return { success: response.status >= 200 && response.status < 300, data: response.data };
  } catch (error) {
    console.error('updateOrder error', error);
    return { success: false, data: getErrorPayload(error) };
  }
};

export const deleteOrder = async (
  organizationId?: string,
  orderId?: string,
): Promise<ApiResult<unknown>> => {
  if (!organizationId || !orderId) {
    return { success: false, data: 'Organization ID or order ID missing' };
  }

  const headers = await getHeaders();
  if (!headers) {
    return { success: false, data: 'Token does not exist' };
  }

  try {
    const response = await axios.delete(`${getOrderBaseEndpoint(organizationId)}${orderId}/`, {
      headers,
    });

    return { success: response.status >= 200 && response.status < 300, data: response.data };
  } catch (error) {
    console.error('deleteOrder error', error);
    return { success: false, data: getErrorPayload(error) };
  }
};

export const addOrderItem = async (
  organizationId?: string,
  orderId?: string,
  itemData?: Partial<OrderItem>,
): Promise<ApiResult<OrderItem | unknown>> => {
  if (!organizationId || !orderId) {
    return { success: false, data: 'Organization ID or order ID missing' };
  }

  const headers = await getHeaders();
  if (!headers) {
    return { success: false, data: 'Token does not exist' };
  }

  try {
    const response = await axios.post(getOrderItemEndpoint(organizationId, orderId), itemData, {
      headers,
    });

    return { success: response.status >= 200 && response.status < 300, data: response.data };
  } catch (error) {
    const payload = getErrorPayload(error);
    console.error('addOrderItem error', payload);
    return { success: false, data: payload };
  }
};

export const updateOrderItem = async (
  organizationId?: string,
  orderId?: string,
  itemId?: string | number,
  payload?: Partial<OrderItem>,
): Promise<ApiResult<OrderItem | unknown>> => {
  if (!organizationId || !orderId || !itemId) {
    return { success: false, data: 'Organization ID, order ID, or item ID missing' };
  }

  const headers = await getHeaders();
  if (!headers) {
    return { success: false, data: 'Token does not exist' };
  }

  try {
    const response = await axios.patch(
      `${getOrderItemEndpoint(organizationId, orderId)}${itemId}/`,
      payload,
      { headers },
    );

    return { success: response.status >= 200 && response.status < 300, data: response.data };
  } catch (error) {
    console.error('updateOrderItem error', error);
    return { success: false, data: getErrorPayload(error) };
  }
};

export const deleteOrderItem = async (
  organizationId?: string,
  orderId?: string,
  itemId?: string | number,
): Promise<ApiResult<unknown>> => {
  if (!organizationId || !orderId || !itemId) {
    return { success: false, data: 'Organization ID, order ID, or item ID missing' };
  }

  const headers = await getHeaders();
  if (!headers) {
    return { success: false, data: 'Token does not exist' };
  }

  try {
    const response = await axios.delete(`${getOrderItemEndpoint(organizationId, orderId)}${itemId}/`, {
      headers,
    });

    return { success: response.status >= 200 && response.status < 300, data: response.data };
  } catch (error) {
    console.error('deleteOrderItem error', error);
    return { success: false, data: getErrorPayload(error) };
  }
};
